/**
 * Parses a backend date array into a correct absolute UTC timestamp (in milliseconds).
 * Handles both UAT (IST local time) and Production (UTC).
 */
export function parseBackendDateArray(
  dateArray: number[] | null | undefined,
  timeZone?: string | null
): number {
  if (!dateArray || dateArray.length < 5) return 0;

  const [year, month, day, hour, minute, second = 0] = dateArray;

  // Use Date.UTC to tell JavaScript this array is UTC
  if (timeZone === 'UTC') {
    return Date.UTC(year, month - 1, day, hour, minute, second);
  }

  // Use new Date to tell JavaScript this array is IST (local time)
  return new Date(year, month - 1, day, hour, minute, second).getTime();
}

const DISPLAY_DATE_PARTS_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const DISPLAY_TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

function toValidDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "DD Mon, YYYY", e.g. "06 Oct, 2026". */
function toDisplayDate(date: Date): string {
  const parts = DISPLAY_DATE_PARTS_FORMAT.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('day')} ${part('month')}, ${part('year')}`;
}

/** Formats a date for display in the app-wide format, e.g. "06 Oct, 2026". */
export function formatDisplayDate(
  value: Date | string | number | null | undefined,
  fallback = '—'
): string {
  const date = toValidDate(value);
  return date ? toDisplayDate(date) : fallback;
}

/** Formats a date with time for display, e.g. "06 Oct, 2026, 10:30 AM". */
export function formatDisplayDateTime(
  value: Date | string | number | null | undefined,
  fallback = '—'
): string {
  const date = toValidDate(value);
  return date ? `${toDisplayDate(date)}, ${DISPLAY_TIME_FORMAT.format(date)}` : fallback;
}

const CLOCK_TIME_FORMAT = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/**
 * Converts an epoch timestamp in seconds (fractional allowed, as sent by the event
 * tracking APIs) to milliseconds. Values that are already in milliseconds pass through.
 */
export function epochToMs(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) return null;
  return value < 1e12 ? Math.round(value * 1000) : value;
}

/** Formats a time of day as 24-hour "HH:mm:ss", e.g. "09:01:36". */
export function formatClockTime(
  value: Date | string | number | null | undefined,
  fallback = '—'
): string {
  const date = toValidDate(value);
  return date ? CLOCK_TIME_FORMAT.format(date) : fallback;
}
